<!--
    contained: render inside a positioned parent and make its other children inert.
    Use #header for the title, the default slot for content, and #footer for actions.
    Set return-focus to the trigger when another dialog can already have focus.
    Example: <Modal contained :visible="open" aria-labelledby="preview-title" @close="open = false">
-->
<template>
    <div
        v-if="contained && visible"
        ref="containment"
        class="custom-modal-containment"
        @click.self="closeModal"
        @keydown.stop="handleKeydown"
    >
        <div
            ref="modal"
            class="custom-modal custom-modal--contained"
            :class="modalClass"
            :style="modalStyle"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="ariaLabelledby || undefined"
            :aria-label="ariaLabel || undefined"
            tabindex="-1"
        >
            <div class="custom-modal__header">
                <div class="custom-modal__title">
                    <slot name="header"></slot>
                </div>
                <button
                    type="button"
                    class="icon-btn icon-btn-white flex-shrink-0"
                    :aria-label="resolvedCloseLabel"
                    @click="closeModal"
                >
                    <i class="fi fi-x" aria-hidden="true"></i>
                </button>
            </div>
            <div class="custom-modal__body">
                <slot></slot>
            </div>
            <div v-if="$slots.footer" class="custom-modal__footer">
                <slot name="footer"></slot>
            </div>
        </div>
    </div>

    <transition v-if="!contained" name="slide-up">
        <div
            v-if="visible"
            ref="modal"
            class="custom-modal"
            :class="modalClass"
            :style="modalStyle"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="ariaLabelledby || undefined"
            :aria-label="ariaLabel || undefined"
            tabindex="-1"
            @keydown="handleKeydown"
        >
            <button
                type="button"
                class="close-btn icon-btn icon-btn-white"
                :aria-label="resolvedCloseLabel"
                @click="closeModal"
            >
                <i class="fi fi-x" aria-hidden="true"></i>
            </button>

            <slot></slot>
        </div>
    </transition>

    <!-- Overlay div to close the modal when clicking outside -->
    <div
        v-if="visible && !contained"
        class="overlay"
        aria-hidden="true"
        @click="closeModal"
    ></div>
</template>

<script>
export default {
    name: 'Modal',
    props: {
        // toggles modal visibility
        visible: {
            type: Boolean,
            required: true,
        },
        contained: {
            type: Boolean,
            default: false,
        },
        returnFocus: {
            type: Object,
            default: null,
        },
        modalClass: {
            type: [String, Array, Object],
            default: '',
        },
        modalStyle: {
            type: [String, Object, Array],
            default: '',
        },
        ariaLabelledby: {
            type: String,
            default: null,
        },
        ariaLabel: {
            type: String,
            default: null,
        },
        closeLabel: {
            type: String,
            default: null,
        },
    },
    emits: ['close'],
    data() {
        return {
            previouslyFocusedElement: null,
            inertElements: [],
        };
    },
    computed: {
        resolvedCloseLabel() {
            if (this.closeLabel) return this.closeLabel;
            if (typeof this.$t === 'function') {
                return this.$t('common.actions.close');
            }
            return 'Close';
        },
    },
    watch: {
        visible(value) {
            if (value) {
                this.focusDialog();
            } else {
                this.restoreFocus();
            }
        },
    },
    mounted() {
        if (this.visible) this.focusDialog();
    },
    beforeUnmount() {
        this.restoreFocus();
    },
    methods: {
        closeModal() {
            this.$emit('close');
        },
        focusDialog() {
            this.previouslyFocusedElement = document.activeElement;
            if (this.contained && this.returnFocus) {
                this.previouslyFocusedElement = this.returnFocus;
            }
            this.$nextTick(() => {
                if (!this.visible || !this.$refs.modal) return;
                const focusableElements = this.getFocusableElements();
                (focusableElements[0] || this.$refs.modal).focus({
                    preventScroll: this.contained,
                });
                if (this.contained) {
                    const container = this.$refs.containment;
                    for (const sibling of container.parentElement.children) {
                        if (sibling !== container && !sibling.inert) {
                            sibling.inert = true;
                            this.inertElements.push(sibling);
                        }
                    }
                    document.addEventListener(
                        'focusin',
                        this.keepFocusInDialog
                    );
                }
            });
        },
        keepFocusInDialog(event) {
            const modal = this.$refs.modal;
            if (this.visible && modal && !modal.contains(event.target)) {
                (this.getFocusableElements()[0] || modal).focus({
                    preventScroll: true,
                });
            }
        },
        restoreFocus() {
            document.removeEventListener('focusin', this.keepFocusInDialog);
            for (const element of this.inertElements) element.inert = false;
            this.inertElements = [];
            const trigger = this.previouslyFocusedElement;
            this.previouslyFocusedElement = null;
            if (this.contained && trigger && document.contains(trigger)) {
                trigger.focus({ preventScroll: true });
                return;
            }
            if (trigger && document.contains(trigger)) trigger.focus();
        },
        getFocusableElements() {
            if (!this.$refs.modal) return [];
            const selector = [
                'a[href]',
                'area[href]',
                'button:not([disabled])',
                'input:not([disabled]):not([type="hidden"])',
                'select:not([disabled])',
                'textarea:not([disabled])',
                'iframe',
                'object',
                'embed',
                '[contenteditable="true"]',
                '[tabindex]:not([tabindex="-1"])',
            ].join(',');
            return Array.from(
                this.$refs.modal.querySelectorAll(selector)
            ).filter(
                (element) =>
                    element.getAttribute('aria-hidden') !== 'true' &&
                    element.getClientRects().length > 0 &&
                    (!this.contained ||
                        (!element.closest('[inert]') &&
                            !element.matches(':disabled') &&
                            element.tabIndex >= 0))
            );
        },
        handleKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                this.closeModal();
                return;
            }
            if (event.key !== 'Tab') return;

            const focusableElements = this.getFocusableElements();
            if (!focusableElements.length) {
                event.preventDefault();
                this.$refs.modal.focus();
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (
                event.shiftKey &&
                (document.activeElement === firstElement ||
                    document.activeElement === this.$refs.modal)
            ) {
                event.preventDefault();
                lastElement.focus();
            } else if (
                !event.shiftKey &&
                document.activeElement === lastElement
            ) {
                event.preventDefault();
                firstElement.focus();
            }
        },
    },
};
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

.custom-modal {
    background-color: white;
    z-index: var(--z-index-modal);
    width: 500px;
    max-width: 95%;
    height: fit-content;
    position: fixed; /* Ensures the modal is positioned relative to the viewport */
    top: 50%; /* Centers the modal vertically */
    left: 50%; /* Centers the modal horizontally */
    transform: translate(-50%, -50%); /* Adjusts for the modal's size */
    border-radius: var(--radius-default);
    padding: var(--space-10) 3rem;
}

/* Contained dialogs move with their parent. Only the body scrolls. */
.custom-modal-containment {
    position: absolute;
    inset: 0;
    z-index: var(--z-index-modal);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-5);
    border-radius: inherit;
    overflow: hidden;
    background: color-mix(in srgb, var(--black) 40%, transparent);
}

.custom-modal.custom-modal--contained {
    position: relative;
    inset: auto;
    transform: none;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    min-width: 0;
    padding: 0;
    background: var(--white);
    border-radius: var(--radius-default);
    box-shadow: 0 var(--space-2) var(--space-6)
        color-mix(in srgb, var(--black) 20%, transparent);
    overflow: hidden;
}

.custom-modal__header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    flex-shrink: 0;
    padding: var(--space-4);
    background: var(--neutral-500);
}

.custom-modal__title {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
}

.custom-modal__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding: var(--space-4);
}

.custom-modal__footer {
    flex-shrink: 0;
    padding: 0 var(--space-4) var(--space-4);
}

.overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /* semi-transparent overlay */
    background-color: rgba(0, 0, 0, 0.1);
    /* one level below the modal */
    z-index: var(--z-index-modal-overlay);
}

.close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
}

@media (max-width: 767.98px) {
    .custom-modal {
        width: 320px;
        padding: var(--space-4) 1rem;
    }

    .custom-modal-containment {
        padding: var(--space-3);
    }
}
</style>
